import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@material-ui/core';
import { CustomBreadcrumbs } from 'candy-commons-components';
import {
    Create,
    Edit,
    SimpleForm,
    SaveButton,
    Toolbar,
    DeleteWithConfirmButton,
    useNotify,
    useTranslate,
} from 'react-admin';
import SpecialInitiativesFields from './SpecialInitiativesFields';
import { prepareSpecialInitiative, validateSpecialInitiative } from './form';

const resource = 'ecommerce-special-initiatives';
const prefix = `resources.${resource}`;

const SpecialInitiativesToolbar = ({ canSave, canDelete, ...props }) => (
    <Toolbar {...props}>
        {canSave && <SaveButton />}
        {canDelete && (
            <DeleteWithConfirmButton
                record={props.record}
                resource={props.resource}
                basePath={props.basePath}
                mutationMode="pessimistic"
                confirmTitle={`${prefix}.delete.title`}
                confirmContent={`${prefix}.delete.message`}
            />
        )}
    </Toolbar>
);

const SpecialInitiativesForm = ({ mode, permissions = {}, ...props }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [conflict, setConflict] = useState(null);
    const permission = permissions[resource] || {};
    const isCreate = mode === 'create';
    const canSave = isCreate ? permission.create : permission.update;
    const canDelete = !isCreate && permission.delete;
    const Controller = isCreate ? Create : Edit;
    const onFailure = (error) => {
        if (error.details?.code === 'SPECIAL_INITIATIVE_COUNTRY_CONFLICT') {
            setConflict(error.details);
        } else {
            notify(error.message || 'ra.notification.http_error', 'warning');
        }
    };

    return (
        <>
            <CustomBreadcrumbs
                data={[
                    {
                        text: translate(`${prefix}.name`),
                        url: resource,
                        icon: <LocalOfferIcon />,
                    },
                    { text: translate(`${prefix}.${mode}.name`), url: '' },
                ]}
            />
            <Controller
                {...props}
                {...(!isCreate ? { mutationMode: 'pessimistic' } : {})}
                transform={prepareSpecialInitiative}
                onFailure={onFailure}
            >
                <SimpleForm
                    redirect="list"
                    validate={validateSpecialInitiative}
                    warnWhenUnsavedChanges
                    toolbar={
                        canSave || canDelete ? (
                            <SpecialInitiativesToolbar
                                canSave={canSave}
                                canDelete={canDelete}
                            />
                        ) : (
                            false
                        )
                    }
                >
                    <SpecialInitiativesFields disabled={!canSave} />
                </SimpleForm>
            </Controller>
            <Dialog
                open={Boolean(conflict)}
                onClose={() => setConflict(null)}
                aria-labelledby="special-initiative-conflict-title"
            >
                <DialogTitle id="special-initiative-conflict-title">
                    {translate(`${prefix}.conflict.title`)}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {translate(`${prefix}.conflict.message`)}
                    </DialogContentText>
                    {conflict?.conflicts?.map((record) => (
                        <DialogContentText key={record.id}>
                            {record.brand}: {record.countries.join(', ')}
                            {permission.update && (
                                <Button
                                    component={Link}
                                    to={`/${resource}/${record.id}`}
                                    color="primary"
                                    onClick={() => setConflict(null)}
                                >
                                    {translate(`${prefix}.conflict.edit`)}
                                </Button>
                            )}
                        </DialogContentText>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConflict(null)} color="primary">
                        {translate(`${prefix}.conflict.close`)}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SpecialInitiativesForm;
